import {
  Briefcase,
  DollarSign,
  GraduationCap,
  MapPin,
  User,
  Brain,
  Target,
  MessageCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

interface PersonaInfoProps {
  persona: {
    profile: any;
    background: any;
    personality: any;
    objections: any;
  };
  className?: string;
}

export function PersonaInfo({ persona, className }: PersonaInfoProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang');
  const scenarioCode = searchParams.get('scenario');
  const [expandedSection, setExpandedSection] = useState<string | null>(
    'profile',
  );

  if (!persona) return null;

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div
      className={`bg-white rounded-xl shadow-sm flex flex-col overflow-hidden ${className}`}
    >
      {/* Section tabs - Fixed at top */}
      <div className="flex border-b border-slate-200 flex-shrink-0 bg-white rounded-t-xl">
        <button
          className={`flex-1 py-2 lg:py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium truncate ${
            expandedSection === 'profile'
              ? 'text-primary border-b-2 border-primary'
              : 'text-slate-600'
          }`}
          onClick={() => toggleSection('profile')}
        >
          {t('custom.roleplay.persona.profile')}
        </button>
        <button
          className={`flex-1 py-2 lg:py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium truncate ${
            expandedSection === 'background'
              ? 'text-primary border-b-2 border-primary'
              : 'text-slate-600'
          }`}
          onClick={() => toggleSection('background')}
        >
          {t('custom.roleplay.persona.background')}
        </button>
        <button
          className={`flex-1 py-2 lg:py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium truncate ${
            expandedSection === 'personality'
              ? 'text-primary border-b-2 border-primary'
              : 'text-slate-600'
          }`}
          onClick={() => toggleSection('personality')}
        >
          {t('custom.roleplay.persona.style')}
        </button>
      </div>

      {/* Content Section - Flexible & Scrollable */}
      <div className="overflow-y-auto flex-1">
        {/* Profile Information */}
        {expandedSection === 'profile' && (
          <div className="p-4 pb-6">
            <h3 className="text-base font-semibold mb-3 text-slate-800">
              {t('custom.roleplay.headers.clientProfile')}
            </h3>

            <div className="space-y-3">
              <div className="flex items-center">
                <User className="w-4 h-4 text-primary mr-3 flex-shrink-0" />
                <div>
                  <span className="text-xs text-slate-500">
                    {t('custom.roleplay.persona.nameAndDemographics')}
                  </span>
                  <p className="text-sm">
                    {persona.profile.name}, {persona.profile.age}
                    {lang == 'ja' ? '歳' : ' • '}
                    {t(
                      persona.profile.gender === 'male'
                        ? 'custom.roleplay.persona.male'
                        : persona.profile.gender === 'female'
                          ? 'custom.roleplay.persona.female'
                          : persona.profile.gender,
                    )}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <Briefcase className="w-4 h-4 text-primary mr-3 flex-shrink-0" />
                <div>
                  <span className="text-xs text-slate-500">
                    {t('custom.roleplay.persona.occupation')}
                  </span>
                  <p className="text-sm">{persona.profile.occupation}</p>
                </div>
              </div>

              {scenarioCode !== 'aia-2' &&
                !scenarioCode?.startsWith('axa-hk-claims') &&
                persona.profile.income && (
                  <div className="flex items-center">
                    <DollarSign className="w-4 h-4 text-primary mr-3 flex-shrink-0" />
                    <div>
                      <span className="text-xs text-slate-500">
                        {t('custom.roleplay.persona.annualIncome')}
                      </span>
                      <p className="text-sm">
                        {lang === 'ko'
                          ? '₩'
                          : lang === 'ja' ||
                              scenarioCode?.startsWith('bnp-paribas-cardif')
                            ? ''
                            : '$'}
                        {persona.profile.income}
                        {lang === 'ja'
                          ? ''
                          : '/' + t('custom.roleplay.persona.year')}
                      </p>
                    </div>
                  </div>
                )}

              {persona.profile.education && (
                <div className="flex items-center">
                  <GraduationCap className="w-4 h-4 text-primary mr-3 flex-shrink-0" />
                  <div>
                    <span className="text-xs text-slate-500">
                      {t('custom.roleplay.persona.education')}
                    </span>
                    <p className="text-sm">{persona.profile.education}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center">
                <MapPin className="w-4 h-4 text-primary mr-3 flex-shrink-0" />
                <div>
                  <span className="text-xs text-slate-500">
                    {t('custom.roleplay.persona.location')}
                  </span>
                  <p className="text-sm">{persona.profile.location}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Background Information */}
        {expandedSection === 'background' && (
          <div className="p-4 pb-6">
            <h3 className="text-base font-semibold mb-3 text-slate-800">
              {t('custom.roleplay.persona.clientBackground')}
            </h3>

            <div className="space-y-4">
              <div>
                <div className="flex items-center mb-1">
                  <Briefcase className="w-4 h-4 text-primary mr-2" />
                  <span className="text-sm font-medium text-slate-700">
                    {t('custom.roleplay.persona.workHistory')}
                  </span>
                </div>
                <p className="text-sm text-slate-600 ml-6 break-words">
                  {persona.background.workHistory}
                </p>
              </div>

              <div>
                <div className="flex items-center mb-1">
                  <DollarSign className="w-4 h-4 text-primary mr-2" />
                  <span className="text-sm font-medium text-slate-700">
                    {t('custom.roleplay.persona.financialSituation')}
                  </span>
                </div>
                <p className="text-sm text-slate-600 ml-6 break-words">
                  {persona.background.financialSituation}
                </p>
              </div>

              <div>
                <div className="flex items-center mb-1">
                  <Brain className="w-4 h-4 text-primary mr-2" />
                  <span className="text-sm font-medium text-slate-700">
                    {t('custom.roleplay.persona.productKnowledge')}
                  </span>
                </div>
                <p className="text-sm text-slate-600 ml-6 break-words">
                  {persona.background.productKnowledge}
                </p>
              </div>

              <div>
                <div className="flex items-center mb-2">
                  <Target className="w-4 h-4 text-primary mr-2" />
                  <span className="text-sm font-medium text-slate-700">
                    {t('custom.roleplay.persona.keyPriorities')}
                  </span>
                </div>
                <ul className="space-y-1 ml-6">
                  {persona.background.priorities.map(
                    (priority: string, index: number) => (
                      <li
                        key={index}
                        className="text-sm text-slate-600 flex items-start"
                      >
                        <span className="text-primary mr-2">•</span>
                        {priority}
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Personality Information */}
        {expandedSection === 'personality' && (
          <div className="p-4 pb-6">
            <h3 className="text-base font-semibold mb-3 text-slate-800">
              {t('custom.roleplay.persona.communicationStyle')}
            </h3>

            <div className="space-y-4">
              <div>
                <div className="flex items-center mb-2">
                  <User className="w-4 h-4 text-primary mr-2" />
                  <span className="text-sm font-medium text-slate-700">
                    {t('custom.roleplay.persona.personalityTraits')}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 ml-6">
                  {persona.personality.traits.map(
                    (trait: string, index: number) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs"
                      >
                        {trait}
                      </span>
                    ),
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center mb-2">
                  <MessageCircle className="w-4 h-4 text-primary mr-2" />
                  <span className="text-sm font-medium text-slate-700">
                    {t('custom.roleplay.persona.communicationStyle')}
                  </span>
                </div>
                <ul className="space-y-1 ml-6">
                  {persona.personality.communication.map(
                    (style: string, index: number) => (
                      <li
                        key={index}
                        className="text-sm text-slate-600 flex items-start"
                      >
                        <span className="text-primary mr-2">•</span>
                        {style}
                      </li>
                    ),
                  )}
                </ul>
              </div>

              <div>
                <div className="flex items-center mb-2">
                  <Brain className="w-4 h-4 text-primary mr-2" />
                  <span className="text-sm font-medium text-slate-700">
                    {t('custom.roleplay.persona.decisionMaking')}
                  </span>
                </div>
                <ul className="space-y-1 ml-6">
                  {persona.personality.decisionMaking.map(
                    (style: string, index: number) => (
                      <li
                        key={index}
                        className="text-sm text-slate-600 flex items-start"
                      >
                        <span className="text-primary mr-2">•</span>
                        {style}
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
